import React from 'react';
import {ReactWizard, WizardPhase} from "../../../components/ReactWizard/ReactWizard";
import {Alert, Button, NavLink} from "reactstrap";
import Utils from "../../../common/Utils";
import FormInput from "../../../components/FormInput/FormInput";
import {UsersEndpoints} from "../../../common/ProxyEndpoints";
import {BusyContent, HeadView, LoadingBars, RegisterComponent} from "../RegisterBase";
import {Form, Formik} from "formik";
import * as Yup from "yup";
import AppVariables from "../../../common/AppVariables";
import {ActivationFormSchema} from "../../ActivateAccount";
import {DevicePlatforms, OwnerAccountType} from "../../../common/Constants";
import DocumentTitle from 'react-document-title';
import PlatformRender from "../../../components/PlatformRender/PlatformRender";
import CodeInputView from "../../../components/CodeInputView/CodeInputView";

const AccountInfoSchema = Yup.object().shape({
    username: Yup.string().required('Please enter a valid username')
        .min(AppVariables.MinUsernameLength)
        .matches(/[a-z]+[a-z0-9@_.\-]+$/,"Username cannot be numeric or contain special characters and whitespaces except \"@\", \".\", \"_\"")
        .matches(/[a-z0-9]$/,"Username can only end with a letter or number")
        .label("Username"),
    password: Yup.string().required('Please enter your password').min(AppVariables.MinPasswordLength).label("Password"),
    password_confirm: Yup.string().required('Confirm password is required').oneOf([Yup.ref('password'),null],"Passwords do not match").label("Confirm Password"),
    account_type: Yup.number().oneOf(Object.values(OwnerAccountType)).label("Account Type")
});

const UserInfoSchema = Yup.object().shape({
    name: Yup.string()
        .trim()
        .required('Please enter a valid name')
        .matches(/[a-zA-Z]{1,}/,"Invalid name. Cannot begin with a number or special characters")
        .matches(/[a-zA-Z0-9]$/,"Name cannot end with symbols or any special character")
        .label('Name'),
    phone: Yup.string()
        .required('Enter a valid phone number')
        .test('phone','Must be a valid phone number', val => Utils.validatePhoneNumber(val))
        .label('Phone'),
    email: Yup.string().email().label('Email'),
    location: Yup.object().nullable().required('Please enter your location').label('Location')
});

const RegisterHead = (props) => {
    return <HeadView title="Register" message="Tractor Owner" info={props.phaseInfo}/>
};


class RegisterTractorOwner extends RegisterComponent {

    constructor(props){
        super(props,'register_tractor_owner');

        const me = this;

        this.setPhaseInfo({

            account_info:{
                index: 0,
                validate: this.onVerifyAccountInfo.bind(this),
                getForm: () => me._accInfoForm
            },

            personal_info: {
                index: 1,
                validate: this.onVerifyPersonalInfo.bind(this),
                getForm: () => me._personalForm
            },

            create_account:{
                index: 2
            },

            activate_account:{
                index: 3,
                validate: this.onActivateAccount.bind(this),
                getForm: () => me._activateForm
            }

        });

    }


    /**
     *  Verify account information
     **/
    onVerifyAccountInfo(){

        const {  username , password , account_type } = this._accInfoForm.getFormikContext().values;
        const accountInfo = {
            username,
            password,
            owner: {
                name: 'unset',
                location: 'unset',
                type : Number(account_type)
            }
        };

        this.persistState('accountInfo',accountInfo);
        return this.processValidation(UsersEndpoints.registerTractorOwnerAccount(true , false , accountInfo ) );
    }


    /**
     * Create account
     */
    onCreateAccount() {

        const personalInfo = this.getStateValue('personalInfo');
        const accountInfo = this.getStateValue('accountInfo');
        const payload =  {
            ...accountInfo,
            ...personalInfo,
            owner: {
                ...accountInfo.owner,
                ...personalInfo.owner
            }
        };

        return this.processRegister(UsersEndpoints.registerTractorOwnerAccount(false, true, payload));
    }


    /**
     *  Verify personal information
     **/
    onVerifyPersonalInfo(){

        const { name , phone , email , location } = this._personalForm.getFormikContext().values;

        let personalInfo = {
            phone: Utils.fixPhone(phone),
            email,
            owner:{
                name,
                location:  location.text,
                location_lat: location.lat,
                location_long: location.lng
            }
        };
        
        this.persistState('personalInfo',personalInfo);
        return this.processValidation(UsersEndpoints.registerTractorOwnerAccount( true, false, personalInfo ));
    }

    /**
     * Activate created account
     */
    onActivateAccount(){
        const { activation_code } = this._activateForm.getFormikContext().values;
        const { username , password }  = this.getStateValue('accountInfo');
        return this.processAccountActivation(activation_code,username,password);
    }

    getWizard(){
        return this._wizard;
    }

    wizardRender(){

        return <DocumentTitle title="Register - Tractor Owner">
            <ReactWizard ref={(wizard) => this._wizard = wizard} onComplete={this.onComplete} sessionKey={"register_owner_wz_session"}>

                {/*Account info phase*/}
                <WizardPhase name="AccountInfo">

                    <Formik initialValues={{ username: '' , password: '' , password_confirm: '' , account_type: OwnerAccountType.Individual }}
                            validationSchema={AccountInfoSchema}
                            ref={r => this._accInfoForm = r}
                            onSubmit={async (values, { setSubmitting }) => {
                                await this.moveNextPhase();
                                setSubmitting(false);
                            }}>

                        {({ isValid }) => (<Form className="card-block p-4 animated fadeIn">

                            <RegisterHead phaseInfo="ACCOUNT INFO" />

                            <div>

                                <FormInput  name="username"
                                            type="text"
                                            title="Username"
                                            disabled={this.state.isBusy}
                                            icon="fa fa-user"
                                            placeholder="Enter your account username here..."
                                            required
                                            showPrompt={true}/>

                                <FormInput  name="password"
                                            type="password"
                                            required={true}
                                            title="Password"
                                            disabled={this.state.isBusy}
                                            icon="fa fa-lock"
                                            placeholder="Enter your password here..."
                                            showPrompt={true}/>

                                <FormInput  name="password_confirm"
                                            type="password"
                                            required={true}
                                            disabled={this.state.isBusy}
                                            title="Confirm password"
                                            icon="fa fa-lock"
                                            placeholder="Confirm your password..."
                                            showPrompt/>

                                <FormInput  name="account_type"
                                            type="select"
                                            disabled={this.state.isBusy}
                                            items={
                                                [
                                                    { value: OwnerAccountType.Individual , text: 'Individual' },
                                                    { value: OwnerAccountType.Company , text: 'Company' }
                                                ]
                                            }
                                            required={true}
                                            title="Account Type"
                                            icon="fa fa-users"
                                            showPrompt={true}/>

                            </div>

                            {this.renderCurrentError()}

                            {this.state.isBusy && <LoadingBars/>}

                            <Button type="submit" color="green" disabled={!isValid || this.state.isBusy} block>Continue <i className="icon-arrow-right"></i></Button>

                            <div className="text-center" style={{'marginTop': '10px'}}>
                                <NavLink href="#" onClick={Utils.navigateLink.bind(this,'/login')}> <i className="icon-arrow-left"></i> <b>Already have an account</b></NavLink>
                            </div>

                        </Form>)}
                    </Formik>

                </WizardPhase>


                {/*Personal info phase*/}
                <WizardPhase name="PersonalInfo">

                   <Formik  initialValues={{ name: '' , phone: '' , email: '' , location: null }}
                            validationSchema={UserInfoSchema}
                            onSubmit={async (values,{ setSubmitting }) => {
                                await this.moveNextPhase();
                                setSubmitting(false);
                            }}
                            ref={r => this._personalForm = r}>
                       {({ isValid }) => {

                           const accountInfo = this.getStateValue('accountInfo');

                           const getNameProps = () => {

                               if(accountInfo && accountInfo.owner) {

                                   switch (accountInfo.owner.type) {
                                       case OwnerAccountType.Company:
                                           return {
                                               title: "Company Name",
                                               placeholder: "Enter your company/organization name"
                                           };
                                       case OwnerAccountType.Individual:
                                           return {
                                               title: "Full Name",
                                               placeholder: "Enter your full name"
                                           };
                                   }
                               }

                           };

                           const getPhoneProps = () => {

                               if(accountInfo && accountInfo.owner){

                                   switch (accountInfo.owner.type) {
                                       case OwnerAccountType.Individual:
                                           return {
                                               placeholder: "Enter your phone number"
                                           };
                                       case OwnerAccountType.Company:
                                           return {
                                               placeholder: "Enter company/organization phone number"
                                           };
                                   }
                               }
                           };

                           const getLocationProps = () => {

                               if(accountInfo && accountInfo.owner){

                                   switch (accountInfo.owner.type) {
                                       case OwnerAccountType.Individual:
                                           return {
                                               placeholder: "Enter your location"
                                           };
                                       case OwnerAccountType.Company:
                                           return {
                                               placeholder: "Enter company/organization location"
                                           };
                                   }

                               }

                           };

                           const getEmailProps = () => {

                               if(accountInfo && accountInfo.owner){
                                   switch (accountInfo.owner.type) {
                                       case OwnerAccountType.Individual:
                                           return {
                                               placeholder: "Enter your email"
                                           };
                                       case OwnerAccountType.Company:
                                           return {
                                               placeholder: "Enter company/organization email"
                                           };
                                   }
                               }
                           };

                           const getPhaseInfoLabel = () => {
                               if(accountInfo && accountInfo.owner){
                                   switch (accountInfo.owner.type) {
                                       case OwnerAccountType.Individual:
                                           return "PERSONAL INFO";
                                       case OwnerAccountType.Company:
                                           return  "COMPANY INFO";
                                   }
                               }
                           };

                           return <Form className="card-block p-4 animated fadeIn">
                               <RegisterHead phaseInfo={getPhaseInfoLabel()}/>

                               <div>

                                   <FormInput  name="name"
                                               type="text"
                                               required={true}
                                               icon="fa fa-user"
                                               disabled={this.state.isBusy}
                                               showPrompt={true}
                                               {...getNameProps()}/>

                                   <FormInput  name="phone"
                                               type="phone"
                                               title="Phone"
                                               required={true}
                                               disabled={this.state.isBusy}
                                               icon="fa fa-phone"
                                               showPrompt={true}
                                               {...getPhoneProps()}/>

                                   <FormInput  name="location"
                                               type="location"
                                               title="Location"
                                               disabled={this.state.isBusy}
                                               required={true}
                                               icon="fa fa-map-marker"
                                               showPrompt={true}
                                               {...getLocationProps()}/>

                                   <hr/>

                                   <FormInput  name="email"
                                               type="email"
                                               title="Email"
                                               required={false}
                                               icon="fa fa-envelope"
                                               disabled={this.state.isBusy}
                                               showPrompt={true}
                                               {...getEmailProps()}/>

                               </div>


                               {this.renderCurrentError()}

                               {this.state.isBusy && <LoadingBars/>}

                               <Button type="submit" color="green" disabled={!isValid || this.state.isBusy} block>Register <i className="fa fa-plus"></i></Button>

                               <div className="text-center" style={{'marginTop': '10px'}}>
                                   <NavLink href="#" onClick={this.movePreviousPhase}><i className="icon-arrow-left"></i> <b>Back to account info</b></NavLink>
                               </div>


                           </Form>
                       }}
                   </Formik>

                </WizardPhase>


                {/* Create account phase */}
                <WizardPhase name="CreateAccount" onActivate={() => this.onCreateAccount() }>

                    <div className="card-block p-4 animated fadeIn" >
                        <BusyContent title="Processing account" message="Please hold on while we prepare your account..." />
                    </div>

                </WizardPhase>

                {/* Activate created account before */}
                <WizardPhase name="ActivateAccount">

                    <div className="card-block p-4 animated fadeIn" >

                        <Formik initialValues={{ activation_code: '' }}
                                validationSchema={ActivationFormSchema}
                                onSubmit={async (values, { setSubmitting }) => {
                                    await this.moveNextPhase();
                                    setSubmitting(false);
                                }}
                                ref={r => this._activateForm = r}>

                            {({ isValid }) => (<Form>

                                <div className="text-center">
                                    <PlatformRender platform={DevicePlatforms.Desktop}>
                                        <h3>Activate Account</h3>
                                    </PlatformRender>

                                    <PlatformRender platform={DevicePlatforms.Mobile}>
                                        <h4>Activate Account</h4>
                                    </PlatformRender>
                                    <p>Just one more step to go...</p>
                                    <hr/>
                                    <Alert color="info"><b>Hint:</b> You've been sent <b>activation code</b>. Enter the code below to proceed.</Alert>
                                </div>

                                <CodeInputView name={"activation_code"} size={0.8} className="mb-2"/>

                                <div>
                                    {this.state.isBusy && <LoadingBars/>}
                                </div>

                                {this.renderCurrentError()}

                                <Button type="submit" color="green" disabled={!isValid || this.state.isBusy}  block>
                                    Activate Account <i className="fa fa-check"></i>
                                </Button>

                                <div className="text-center" style={{'marginTop': '10px'}}>
                                    <NavLink href="#" onClick={Utils.navigateLink.bind(this,'/login')}> <i className="icon-arrow-left"></i> <b>Back to Login</b></NavLink>
                                </div>

                            </Form>)}

                        </Formik>

                    </div>

                </WizardPhase>

            </ReactWizard>
        </DocumentTitle>

    }

}


export default RegisterTractorOwner;


// WEBPACK FOOTER //
// ./src/views/Register/TractorOwner/RegisterTractorOwner.js